import React, { useState } from "react";
import { BiMale, BiMaleSign } from "react-icons/bi";
import { FaFileInvoiceDollar, FaSignOutAlt, FaPrint, FaMoneyBillWave, FaUserCircle, FaMale, FaFemale } from "react-icons/fa";
import { GrUser, GrUserFemale } from "react-icons/gr";
import Modal from "../../components/Modal";
import Receipt from "./receipt";
import { formatToZMW } from "../../helpers";

const Dash = ({allData, user, receipts, onPrint }) => {
  const [showAllReceipts, setShowAllReceipts] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Calculate total income from receipts
  const totalIncome = receipts.reduce(
    (sum, receipt) =>
      sum +
      receipt.transactions.reduce((tSum, t) => tSum + parseFloat(t.amount), 0),
    0
  );

  // Handle print last receipt
  const handlePrintLastReceipt = () => {
    if (receipts.length > 0) {
      setSelectedReceipt(receipts[0]);
      setShowReceiptModal(true);
    }
  };

  // Handle view all receipts
  const handleViewAllReceipts = () => {
    setShowAllReceipts(true);
  };

  // Handle print specific receipt
  const handlePrintReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowReceiptModal(true);
  };

  return (
    <div className="min-h-screen flex flex-col items-center p-4 text-white">
      {/* User Info Section */}
      <div className="w-full max-w-md bg-white text-gray-800 shadow-lg rounded-lg p-6 flex flex-col items-center">
        <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center text-3xl font-bold text-blue-500">
          {user?.sex.toLowerCase() === 'male'?<GrUser/>:<GrUserFemale/>}
        </div>
        <h1>{allData.council[0]?.name}</h1>
        <h2 className="text-lg font-bold mt-2">Welcome, {user?.firstname}!</h2>
        <p className="text-gray-500">Your financial summary at a glance</p>
      </div>

      {/* Stats Section */}
      <div className="w-full max-w-md grid grid-cols-2 gap-4 mt-4">
        <div className="bg-white text-gray-800 p-4 rounded-lg shadow-lg flex flex-col items-center">
          <FaFileInvoiceDollar className="text-blue-500 text-3xl" />
          <p className="text-sm text-gray-500">Total Receipts</p>
          <h3 className="text-lg font-bold">{receipts.length}</h3>
        </div>
        <div className="bg-white text-gray-800 p-4 rounded-lg shadow-lg flex flex-col items-center">
          <FaMoneyBillWave className="text-blue-500 text-3xl" />
          <p className="text-sm text-gray-500">Total Income</p>
          <h3 className="text-lg font-bold">K{formatToZMW(totalIncome)}</h3>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="w-full max-w-md mt-4 flex flex-col gap-3">
        <button 
          onClick={handleViewAllReceipts}
          className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white p-3 rounded-md shadow-md hover:bg-blue-700 transition-all duration-300"
        >
          <FaFileInvoiceDollar /> View All Receipts
        </button>
        <button
          onClick={handlePrintLastReceipt}
          disabled={receipts.length === 0}
          className={`w-full flex items-center justify-center gap-2 p-3 rounded-md shadow-md transition-all duration-300 ${
            receipts.length === 0 
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
              : 'bg-blue-500 text-white hover:bg-blue-700'
          }`}
        >
          <FaPrint /> Print Last Receipt
        </button>
      </div>

      {/* All Receipts Modal */}
      {showAllReceipts && (
        <Modal title="All Receipts" onClose={() => setShowAllReceipts(false)}>
          <div className="max-h-96 overflow-y-auto">
            {receipts.length > 0 ? (
              <div className="space-y-3">
                {receipts.sort((a, b) => new Date(b.date) - new Date(a.date)).map((receipt, index) => (
                  <div 
                    key={receipt.voucher} 
                    className="bg-gray-100 p-4 rounded-lg flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => handlePrintReceipt(receipt)}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">
                        Receipt #{receipt.voucher}
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(receipt.date).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-600">
                        {receipt.paye}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600">
                        K{receipt.transactions.reduce((sum, t) => sum + parseFloat(t.amount), 0).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {receipt.transactions.length} items
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaFileInvoiceDollar className="text-gray-400 text-4xl mx-auto mb-4" />
                <p className="text-gray-600">No receipts found</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Receipt Print Modal */}
      {showReceiptModal && selectedReceipt && (
        <Modal title="Print Receipt" onClose={() => setShowReceiptModal(false)}>
          <Receipt 
            receipt={selectedReceipt} 
            council={allData.council[0]?.name} 
          />
        </Modal>
      )}
    </div>
  );
};

export default Dash;